/**
 * Format Utilities
 *
 * Centralized formatting functions for consistent data presentation across the application.
 * Use these instead of inline formatting logic.
 */

/**
 * The locale every formatter in this module reads by default.
 *
 * It is the signed-in user's own `languageCode`, published here once the
 * session loads (see `services/login/auth-helpers`), so a Brazilian reader
 * gets `24 de ago. de 2026` instead of the `en-US` this module used to pin.
 *
 * Module state is safe here only because it is client state: every screen that
 * formats is a client component. The guards below make that structural rather
 * than incidental — on the server there is no single viewer, so writing is an
 * error and reading falls back to the default. Without them, one company's
 * locale could be rendered into another company's page.
 */
const DEFAULT_LOCALE = 'en-US';

let displayLocale = DEFAULT_LOCALE;

/** Publish the signed-in user's locale. Client-side only, by construction. */
export function setDisplayLocale(languageCode: string | null | undefined): void {
  if (typeof window === 'undefined') {
    throw new Error(
      'setDisplayLocale is client-only: module state on the server would leak one viewer\'s locale into another\'s response.',
    );
  }
  displayLocale = languageCode?.trim() || DEFAULT_LOCALE;
}

/** The locale to format with, or the default when there is no single viewer. */
export function getDisplayLocale(): string {
  return typeof window === 'undefined' ? DEFAULT_LOCALE : displayLocale;
}

/**
 * Format a number as currency.
 *
 * @param value - The numeric value to format (handles undefined/null/NaN gracefully)
 * @param currencyCode - ISO 4217 currency code (default: 'USD')
 * @param locale - BCP 47 locale string (default: 'en-US')
 * @returns Formatted currency string (e.g., "$1,234.56") or fallback for invalid values
 *
 * @example
 * formatCurrency(1234.5) // "$1,234.50"
 * formatCurrency(1234.5, 'EUR', 'de-DE') // "1.234,50 €"
 * formatCurrency(undefined) // "$0.00"
 */
export function formatCurrency(
  value: number | undefined | null,
  currencyCode: string = 'USD',
  locale: string = getDisplayLocale(),
  fractionDigits = 2
): string {
  const safeValue = typeof value === 'number' && !Number.isNaN(value) ? value : 0;

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(safeValue);
}

/**
 * Format a metered amount — a per-call or per-token cost, as opposed to a price
 * the customer is charged.
 *
 * This exists apart from `formatCurrency` because the two have genuinely
 * different precision needs and one function cannot serve both: a charge is
 * always two decimals, while a metered cost is routinely below a cent. Fixing
 * two decimals there renders a real cost as "$0.00", and fixing six pads every
 * ordinary amount with dead zeros ("$12.340000").
 *
 * The precision therefore follows the magnitude, and the minimum stays at two
 * so amounts still line up as money:
 *
 *   12.3456   -> "$12.35"
 *    0.0567   -> "$0.0567"
 *    0.0034   -> "$0.0034"
 *    0.0000012 -> "$0.000001"
 *
 * @param value - The amount (undefined/null/NaN are treated as 0)
 * @param currencyCode - ISO 4217 currency code (default: 'USD')
 * @param locale - BCP 47 locale string (default: 'en-US')
 */
export function formatCost(
  value: number | undefined | null,
  currencyCode: string = 'USD',
  locale: string = getDisplayLocale()
): string {
  const safeValue = typeof value === 'number' && !Number.isNaN(value) ? value : 0;
  const magnitude = Math.abs(safeValue);
  const maximumFractionDigits = magnitude >= 1 ? 2 : magnitude >= 0.01 ? 4 : 6;

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits,
  }).format(safeValue);
}

/**
 * Format a date/timestamp for display.
 *
 * @param value - Date string, Date object, or timestamp (handles null/undefined)
 * @param format - Predefined format: 'short', 'long', 'datetime', 'relative' (default: 'short')
 * @param locale - BCP 47 locale string (default: 'en-US')
 * @returns Formatted date string or fallback for invalid values
 *
 * @example
 * formatDate('2025-01-15') // "Jan 15, 2025"
 * formatDate('2025-01-15', 'long') // "January 15, 2025"
 * formatDate('2025-01-15T10:30:00', 'datetime') // "Jan 15, 2025, 10:30 AM"
 * formatDate(null) // "-"
 */
export function formatDate(
  value: string | Date | number | undefined | null,
  format: 'short' | 'long' | 'datetime' | 'relative' = 'short',
  locale: string = getDisplayLocale(),
  /**
   * IANA time zone. Billing screens pass 'UTC' on purpose: a billing period is
   * anchored to UTC, so rendering it in the reader's zone can move an invoice
   * date across a day boundary. Everything else wants the reader's own zone,
   * which is what leaving this undefined gives.
   */
  timeZone?: string
): string {
  if (value === null || value === undefined) {
    return '-';
  }

  const normalizedValue =
    typeof value === 'number'
      // API timestamps are Unix seconds; JS Date expects milliseconds.
      ? (value < 1e12 ? value * 1000 : value)
      : value;
  const date = normalizedValue instanceof Date ? normalizedValue : new Date(normalizedValue);

  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  const formatOptions: Record<string, Intl.DateTimeFormatOptions> = {
    short: { month: 'short', day: 'numeric', year: 'numeric' },
    long: { month: 'long', day: 'numeric', year: 'numeric' },
    datetime: {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    },
    relative: { month: 'short', day: 'numeric', year: 'numeric' }, // Fallback - relative handled separately
  };

  if (format === 'relative') {
    return formatRelativeDate(date, locale);
  }

  return new Intl.DateTimeFormat(locale, { ...formatOptions[format], timeZone }).format(date);
}

/**
 * Format a date relative to now (e.g., "2 days ago", "in 3 hours")
 */
function formatRelativeDate(date: Date, locale: string = getDisplayLocale()): string {
  const now = new Date();
  const diffInMs = date.getTime() - now.getTime();
  const diffInSeconds = Math.round(diffInMs / 1000);
  const diffInMinutes = Math.round(diffInSeconds / 60);
  const diffInHours = Math.round(diffInMinutes / 60);
  const diffInDays = Math.round(diffInHours / 24);

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (Math.abs(diffInDays) >= 1) {
    return rtf.format(diffInDays, 'day');
  }
  if (Math.abs(diffInHours) >= 1) {
    return rtf.format(diffInHours, 'hour');
  }
  if (Math.abs(diffInMinutes) >= 1) {
    return rtf.format(diffInMinutes, 'minute');
  }
  return rtf.format(diffInSeconds, 'second');
}

/**
 * Format a Unix-seconds timestamp as a compact relative string.
 *
 * Unlike `formatDate(…, 'relative')` which uses `Intl.RelativeTimeFormat`,
 * this produces short labels suited for card metadata (e.g., "5m ago", "2h ago", "3d ago").
 *
 * @param timestamp - Unix timestamp in **seconds** (not milliseconds). Returns `fallback` when null/undefined.
 * @param fallback  - Text returned for missing timestamps (default: `'Never'`).
 *
 * @example
 * formatRelativeTime(Date.now() / 1000 - 120) // "2m ago"
 * formatRelativeTime(null)                     // "Never"
 * formatRelativeTime(null, 'Not published yet') // "Not published yet"
 */
export function formatRelativeTime(
  timestamp: number | null | undefined,
  fallback = 'Never',
): string {
  if (timestamp == null) return fallback;

  const diffMs = Date.now() - timestamp * 1000;
  if (diffMs < 0 || diffMs < 60_000) return 'Just now';
  if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)}m ago`;
  if (diffMs < 86_400_000) return `${Math.floor(diffMs / 3_600_000)}h ago`;
  const days = Math.floor(diffMs / 86_400_000);
  if (days < 30) return `${days}d ago`;
  return new Date(timestamp * 1000).toLocaleDateString();
}

/**
 * Format a number in compact notation (e.g., 1.2K, 3.5M).
 *
 * @param value - The numeric value to format.
 * @returns Compact string representation.
 *
 * @example
 * formatCompactCount(1234)   // "1.2K"
 * formatCompactCount(42)     // "42"
 */
export function formatCompactCount(value: number): string {
  return new Intl.NumberFormat(undefined, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * Format a number with thousands separators.
 *
 * @param value - The numeric value to format
 * @param locale - BCP 47 locale string (default: 'en-US')
 * @returns Formatted number string (e.g., "1,234,567")
 *
 * @example
 * formatNumber(1234567) // "1,234,567"
 * formatNumber(1234567, 'de-DE') // "1.234.567"
 */
export function formatNumber(
  value: number | undefined | null,
  locale: string = getDisplayLocale()
): string {
  const safeValue = typeof value === 'number' && !Number.isNaN(value) ? value : 0;
  return new Intl.NumberFormat(locale).format(safeValue);
}

/**
 * Format a percentage value.
 *
 * @param value - The numeric value (0-100 or 0-1 depending on isDecimal)
 * @param decimalPlaces - Number of decimal places (default: 1)
 * @param isDecimal - Whether value is already decimal (0-1) vs percentage (0-100) (default: false)
 * @returns Formatted percentage string (e.g., "75.5%")
 *
 * @example
 * formatPercentage(75.5) // "75.5%"
 * formatPercentage(0.755, 1, true) // "75.5%"
 */
export function formatPercentage(
  value: number | undefined | null,
  decimalPlaces: number = 1,
  isDecimal: boolean = false
): string {
  const safeValue = typeof value === 'number' && !Number.isNaN(value) ? value : 0;
  const percentValue = isDecimal ? safeValue * 100 : safeValue;
  return `${percentValue.toFixed(decimalPlaces)}%`;
}

/**
 * Humanize snake_case or kebab-case identifiers for UI labels.
 */
export function humanizeIdentifier(value: string | null | undefined): string {
  if (!value) {
    return '-';
  }

  return value
    .split(/[_-]+/g)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

/**
 * Format a byte count for display.
 *
 * The six copies this replaces all stopped at MB, so a 2 GB upload read as
 * "2048.0 MB", and only one of them handled a missing size.
 *
 * Digits go through `Intl` rather than `toFixed`, which is what a seventh copy
 * (`formatMetricBytes`, on the API-key metrics panel) got right and the rest
 * did not: it groups thousands in the bytes range ("1,023 B") and drops a
 * trailing ".0", so a round value reads "2 KB" instead of "2.0 KB".
 *
 * @param bytes - Size in bytes (undefined/null/negative render as a dash)
 * @param locale - BCP 47 locale string (default: 'en-US')
 */
export function formatFileSize(bytes: number | undefined | null, locale: string = getDisplayLocale()): string {
  if (typeof bytes !== 'number' || Number.isNaN(bytes) || bytes < 0) {
    return '-';
  }
  if (bytes < 1024) {
    return `${new Intl.NumberFormat(locale).format(bytes)} B`;
  }

  const units = ['KB', 'MB', 'GB', 'TB'];
  let size = bytes / 1024;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  const digits = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(size);
  return `${digits} ${units[unitIndex]}`;
}
